import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 全屏看图：双指缩放、拖动平移、下滑关闭、左右翻页。
 *
 * 这个组件存在的唯一理由是**手势消歧**——四种手势挤在同一块区域里，判据写错一条，
 * 用户就会在放大看细节的时候莫名其妙翻到下一张。
 *
 * 判据（每一条都对应一个踩过的坑）：
 * - **放大后（scale > 1.03）横向拖动 = 平移图片**，不是翻页；
 * - 未放大时横向拖动才是翻页，且要求 `|dx| > 阈值` **且** `|dx| > |dy| * 1.5`
 *   ——最后这条是防"下滑关闭"被误判成翻页；
 * - 未放大时竖直下滑 = 关闭，且跟手（边拖边缩小变透明），不跟手就完全不像原生；
 * - 双击 = 在 1x 和 2.5x 之间切换，这是所有原生看图器的通用约定。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlay } from '../hooks/overlay.js';
const SWIPE_THRESHOLD = 60;
const AXIS_RATIO = 1.5;
const CLOSE_THRESHOLD = 110;
const ZOOMED = 1.03;
const DOUBLE_TAP_MS = 280;
/** 判定"这是手势而不是点击"的位移阈值（px）。见 onPointerDown 里为什么要推迟捕获。 */
const DRAG_SLOP = 6;
const IDENTITY = { scale: 1, x: 0, y: 0 };
export function ImageViewer({ open, onClose, images, index = 0, onIndexChange }) {
    const rootRef = useRef(null);
    const [current, setCurrent] = useState(index);
    const [t, setT] = useState(IDENTITY);
    const [closeProgress, setCloseProgress] = useState(0);
    const [dragging, setDragging] = useState(false);
    const pointers = useRef(new Map());
    const gesture = useRef({ mode: 'none', startT: IDENTITY, startX: 0, startY: 0, startDist: 0, lastTapAt: 0, captured: false });
    // 看图器本身就是全屏的，不需要额外锁滚动；但焦点、Esc、安卓返回键都要
    useOverlay({ active: open, onClose, containerRef: rootRef, lockScroll: false });
    useEffect(() => {
        if (open) {
            setCurrent(index);
            setT(IDENTITY);
            setCloseProgress(0);
        }
    }, [open, index]);
    const go = useCallback((next) => {
        const clamped = Math.max(0, Math.min(images.length - 1, next));
        if (clamped === current)
            return;
        setCurrent(clamped);
        setT(IDENTITY); // 翻页必须重置缩放，否则下一张一进来就是放大且偏移的
        onIndexChange?.(clamped);
    }, [current, images.length, onIndexChange]);
    const onPointerDown = useCallback((e) => {
        // 点在关闭按钮/计数器这类控件上时，整个手势都不该开始——让按钮自己处理。
        if (e.target.closest('button'))
            return;
        // ⚠️ **这里不 setPointerCapture。** 捕获会把兼容 click 重定向到捕获元素，
        // 于是它的子元素（右上角那个关闭按钮）**永远点不动**，而视觉上完全正常。（已复现。）
        // 捕获推迟到真的开始移动、或者第二根手指落下（捏合）时再做。
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const g = gesture.current;
        g.startT = t;
        if (pointers.current.size === 2) {
            // 两根手指落下已经不可能是点击了，直接捕获
            e.currentTarget.setPointerCapture?.(e.pointerId);
            g.captured = true;
            const [a, b] = [...pointers.current.values()];
            g.mode = 'pinch';
            g.startDist = Math.hypot((b?.x ?? 0) - (a?.x ?? 0), (b?.y ?? 0) - (a?.y ?? 0)) || 1;
            return;
        }
        // 双击缩放：原生看图器的通用约定
        const now = performance.now();
        if (now - g.lastTapAt < DOUBLE_TAP_MS) {
            g.lastTapAt = 0;
            setT(t.scale > ZOOMED ? IDENTITY : { scale: 2.5, x: 0, y: 0 });
            g.mode = 'none';
            return;
        }
        g.lastTapAt = now;
        g.startX = e.clientX;
        g.startY = e.clientY;
        // 放大了就是平移，没放大就等移动方向出来了再判是翻页还是关闭
        g.mode = t.scale > ZOOMED ? 'pan' : 'none';
    }, [t]);
    /*
     * 桌面输入：方向键翻页、+/- 与滚轮缩放、0 复位。
     *
     * ⚠️ 不做这一条的表现不是"少了个快捷方式"，而是**鼠标用户根本没法用**：
     * 翻页要靠横向拖拽（鼠标上很别扭）、缩放要靠双指捏合（鼠标上根本没有）。
     * 一个只能用手指操作的看图器在 PC 上等于坏的。
     */
    useEffect(() => {
        if (!open || typeof window === 'undefined')
            return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                go(current - 1);
            }
            else if (e.key === 'ArrowRight') {
                e.preventDefault();
                go(current + 1);
            }
            else if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                setT((prev) => ({ ...prev, scale: Math.min(5, prev.scale * 1.3) }));
            }
            else if (e.key === '-') {
                e.preventDefault();
                setT((prev) => {
                    const scale = Math.max(1, prev.scale / 1.3);
                    return scale <= ZOOMED ? IDENTITY : { ...prev, scale };
                });
            }
            else if (e.key === '0') {
                e.preventDefault();
                setT(IDENTITY);
            }
            // Esc 由 useOverlay 负责，别在这里抢
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, current, go]);
    /**
     * 滚轮缩放（桌面）。
     *
     * ⚠️ **必须用原生监听 + `{ passive: false }`，不能用 React 的 `onWheel`。**
     * React 把 wheel / touchstart / touchmove 注册成**被动监听**，
     * 里面调 `preventDefault()` 不但无效，还会在控制台刷一条
     * "Unable to preventDefault inside passive event listener invocation"。
     * 表现是：图片确实缩放了，但**页面同时也在缩放/滚动**——
     * Mac 上触控板捏合会把整个浏览器页面一起放大，看起来像彻底失控。
     *
     * ⚠️ 触控板的双指捏合在浏览器里就是**带 ctrlKey 的 wheel**，所以这一条同时接管了它。
     */
    useEffect(() => {
        const el = rootRef.current;
        if (!open || !el)
            return;
        const onWheel = (e) => {
            e.preventDefault();
            setT((prev) => {
                const factor = Math.exp(-e.deltaY / (e.ctrlKey ? 100 : 300));
                const scale = Math.max(1, Math.min(5, prev.scale * factor));
                // 缩回 1x 就顺手把平移也复位，否则会停在一个偏移过的 1x 上
                return scale <= ZOOMED ? IDENTITY : { ...prev, scale };
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open]);
    const onPointerMove = useCallback((e) => {
        if (!pointers.current.has(e.pointerId))
            return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const g = gesture.current;
        // 超过滑动阈值才算手势开始，这时候再捕获指针（见 onPointerDown 的注释）
        if (!g.captured) {
            const moved = Math.hypot(e.clientX - g.startX, e.clientY - g.startY);
            if (moved <= DRAG_SLOP)
                return;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            g.captured = true;
            setDragging(true);
        }
        if (g.mode === 'pinch' && pointers.current.size === 2) {
            const [a, b] = [...pointers.current.values()];
            const dist = Math.hypot((b?.x ?? 0) - (a?.x ?? 0), (b?.y ?? 0) - (a?.y ?? 0));
            const scale = Math.max(1, Math.min(5, g.startT.scale * (dist / g.startDist)));
            setT((prev) => ({ ...prev, scale }));
            return;
        }
        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        if (g.mode === 'pan') {
            setT({ scale: g.startT.scale, x: g.startT.x + dx, y: g.startT.y + dy });
            return;
        }
        if (g.mode === 'none') {
            // 方向还没定：等位移足够大再判，太早判会把手抖当成手势
            if (Math.hypot(dx, dy) < 12)
                return;
            // 竖直向下且明显压过横向 → 下滑关闭
            if (dy > 0 && Math.abs(dy) > Math.abs(dx) * AXIS_RATIO)
                g.mode = 'close';
            // 横向且明显压过竖直 → 翻页
            else if (Math.abs(dx) > Math.abs(dy) * AXIS_RATIO)
                g.mode = 'swipe';
            else
                return;
        }
        if (g.mode === 'close') {
            const progress = Math.min(1, Math.max(0, dy) / (CLOSE_THRESHOLD * 2));
            setCloseProgress(progress);
            // 跟手：边拖边缩小并变透明。不跟手的话"下滑关闭"就只是个开关，不像原生。
            setT({ scale: 1 - progress * 0.25, x: dx * 0.5, y: dy });
        }
    }, []);
    const onPointerUp = useCallback((e) => {
        const g = gesture.current;
        if (g.captured)
            e.currentTarget.releasePointerCapture?.(e.pointerId);
        const last = pointers.current.get(e.pointerId);
        pointers.current.delete(e.pointerId);
        setDragging(false);
        if (pointers.current.size === 0)
            g.captured = false;
        if (g.mode === 'pinch') {
            // 缩回 1x 以下就吸附回原位
            if (t.scale <= ZOOMED)
                setT(IDENTITY);
            if (pointers.current.size === 0)
                g.mode = 'none';
            return;
        }
        const dx = (last?.x ?? g.startX) - g.startX;
        const dy = (last?.y ?? g.startY) - g.startY;
        if (g.mode === 'close') {
            if (dy > CLOSE_THRESHOLD) {
                onClose();
            }
            else {
                setT(IDENTITY);
                setCloseProgress(0);
            }
        }
        else if (g.mode === 'swipe') {
            // ⚠️ 这里再确认一次"没放大"：放大状态下不该走到这个分支，但多一道闸不吃亏
            if (t.scale <= ZOOMED && Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * AXIS_RATIO) {
                go(dx > 0 ? current - 1 : current + 1);
            }
        }
        g.mode = 'none';
    }, [current, go, onClose, t.scale]);
    if (!open || images.length === 0)
        return null;
    const image = images[Math.max(0, Math.min(images.length - 1, current))];
    return (_jsxs("div", { ref: rootRef, className: "ps-viewer", role: "dialog", "aria-modal": "true", "aria-label": "\u56FE\u7247\u67E5\u770B", tabIndex: -1, style: { background: `rgb(0 0 0 / ${(1 - closeProgress) * 96}%)` }, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp, children: [_jsx("button", { type: "button", className: "ps-viewer-close", onClick: onClose, "aria-label": "\u5173\u95ED", children: "\u2715" }), images.length > 1 && (_jsxs("div", { className: "ps-viewer-counter", children: [current + 1, " / ", images.length] })), _jsx("img", { className: "ps-viewer-img", src: image?.src, alt: image?.alt ?? '', draggable: false, style: {
                    transform: `translate3d(${t.x}px, ${t.y}px, 0) scale(${t.scale})`,
                    transition: dragging ? 'none' : 'transform var(--ps-t-base) var(--ps-ease-out)',
                } })] }));
}
//# sourceMappingURL=image-viewer.js.map